import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useSession } from '../SessionProvider';
import {
  findTargetWithNameFallback,
  getTargetInfoAtPixel,
} from './getShipInfo';
import { ShipInfoByPropertiesProps, FindTargetResult } from '../shipInfo';
import { ExtractedTargetInfo } from '../extractTargetInfo';

export const useTargetInfoAtPixel = (
  coordinate: { x: number; y: number } | null,
  selectTarget: boolean,
  deselectOtherTargets: boolean,
  shouldFetch: boolean,
  replay: boolean,
): UseQueryResult<ExtractedTargetInfo | null> => {
  const { olSession, replaySession } = useSession();

  const session = replay && replaySession ? replaySession : olSession;

  return useQuery(
    {
      queryKey: ['shipInfoAtPixel', coordinate],
      queryFn: () => {
        // This check is necessary, so that hovering doesn't interfere with clicking of a ship
        if (!shouldFetch) {
          return Promise.resolve(null);
        }
        if (coordinate !== null) {
          const targetInfo = getTargetInfoAtPixel(
            session,
            coordinate.x,
            coordinate.y,
            selectTarget,
            deselectOtherTargets,
          );

          return targetInfo;
        }
        return Promise.resolve(null);
      },
      enabled: coordinate !== null && shouldFetch,
    },
    'shipInfo:atPixel',
  );
};

export const useFindTargetByProperties = (
  props: ShipInfoByPropertiesProps | null,
): UseQueryResult<FindTargetResult | null> => {
  const { olSession } = useSession();

  return useQuery(
    {
      queryKey: ['findTarget', props],
      queryFn: () => {
        if (props !== null) {
          return findTargetWithNameFallback(olSession, props);
        }
        return Promise.resolve(null);
      },
      enabled: props !== null,
    },
    'shipInfo:findByProperties',
  );
};
